import React from 'react';
import {
  ELIG_TOKENS,
  READINESS_FILLED,
  READINESS_TONE,
} from '@features/eligibility-checks/theme/readinessTokens';
import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { motion } from 'motion/react';

import { AppointmentPropertiesTable } from '../../../../EligibilityDrawer/AppointmentPropertiesTable';
import { SendInvoiceButton } from '../../../../EligibilityDrawer/SendInvoiceButton';
import type { ExpandedCardProps } from '../types';

import { ActionButtons } from './ActionButtons';
import {
  ActionFooter,
  ContentColumn,
  ExpandedCardWrapper,
  Icon,
  MainContentRow,
  PatientName,
} from './ExpandedCard.styles';

/**
 * ExpandedCard — pure presentation. Floating UI in EventComponent owns the
 * portal, positioning, hover lifecycle, dismissal, and engaged-state tracking.
 */
export const ExpandedCard: React.FC<ExpandedCardProps> = ({
  event,
  icon,
  cssVars,
  variant,
  actionMessage,
  isPopoverHovered = false,
  onOpenInDrawer,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.12 }}
    >
      <ExpandedCardWrapper
        sx={{
          ...cssVars,
          position: 'relative',
          width: 320,
          maxWidth: '90vw',
          transition: 'outline-width 0.12s ease-out, box-shadow 0.12s ease-out',
          ...(isPopoverHovered && {
            '--event-expanded-outline-width': '3px',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.16)',
          }),
        }}
      >
        {/* Engaged-state-only "Open in drawer" affordance. Hidden during
            scanning so it doesn't add noise; appears once the user settles
            into the popover. */}
        {onOpenInDrawer && (
          <Tooltip title="Open full detail" placement="left" arrow>
            <IconButton
              size="small"
              onClick={onOpenInDrawer}
              aria-label="Open full detail"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 24,
                height: 24,
                opacity: isPopoverHovered ? 1 : 0,
                transition: 'opacity 0.12s ease-out',
                pointerEvents: isPopoverHovered ? 'auto' : 'none',
                color: ELIG_TOKENS.inkSoft,
                '&:hover': { color: ELIG_TOKENS.midnight },
              }}
            >
              <OpenInFullOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <MainContentRow>
          {icon && <Icon component={icon} />}
          <ContentColumn>
            {/* Readiness verb — leads the card when block_state.readiness is
                set. For "collect", an inline Send-invoice CTA sits at the
                end of the row so the action is one click away from hover. */}
            {(() => {
              const readiness = event.resource.block_state?.readiness;
              const label = event.resource.block_state?.readiness_label;
              if (!readiness || !label) return null;
              const tone = READINESS_TONE[readiness];
              const filled = READINESS_FILLED[readiness];
              const color = ELIG_TOKENS[tone];
              const isVerified = readiness === 'verified';
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      ...(filled
                        ? { bgcolor: color }
                        : { border: `1.5px solid ${color}`, bgcolor: 'transparent' }),
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color,
                      fontWeight: isVerified ? 400 : 600,
                      ...(isVerified
                        ? {}
                        : {
                            textDecoration: 'underline',
                            textDecorationThickness: '1.5px',
                          }),
                    }}
                  >
                    {label}
                  </Typography>
                  {readiness === 'collect' && (
                    <SendInvoiceButton
                      appointment={event.resource}
                      variant="inline"
                      source="eligibility_popover"
                    />
                  )}
                </Box>
              );
            })()}

            <PatientName variant="h6">{event.title}</PatientName>

            {/* Compact properties subset for quick-glance editing.
                EventComponent mounts a `<FloatingTree>` so each row's
                click-to-edit popover registers as a tree descendant — that
                keeps the parent's safe-polygon and outside-press dismiss
                logic from closing this popover when interacting with a
                nested editor. */}
            <Box sx={{ mt: 0.5 }}>
              <AppointmentPropertiesTable
                appointment={event.resource}
                rows={['visit_status', 'provider', 'type', 'time', 'notes']}
              />
            </Box>
          </ContentColumn>
        </MainContentRow>

        {variant === 'actionable' && actionMessage && (
          <ActionFooter>{actionMessage}</ActionFooter>
        )}
      </ExpandedCardWrapper>
    </motion.div>
  );
};

// ActionButtons is unused in the experimental route (modal mode is gated by
// read-only) — keep the import alive so we don't break the dead code path.
export { ActionButtons as _ActionButtons };
